/**
 * Product Email Config Page
 * Configuração de email por produto: cada produto tem sua própria API key (Resend) e domínio,
 * em vez de compartilhar a credencial única do core geral (vover.com.br).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  RadioGroup,
  FormControl,
  FormLabel,
  Radio,
  Divider,
  Card,
  CardContent,
  Stack,
  Alert,
  Chip
} from '@mui/material';
import { apiService } from '@/services/api';
import { useMessage } from '@/contexts/MessageContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useFilters } from '@/contexts/FilterContext';
import { ProductCell } from '@/components/ProductCell';
import { useProductsMap } from '@/hooks/useProductsMap';
import { logger } from '@/utils/logger';
import { ProductScopeGate } from '@/components/ProductScopeGate';

const EMPTY_CONFIG = {
  enabled: false,
  useApi: true,
  smtp: { host: '', port: 587, secure: false, auth: { user: '', pass: '' } },
  sender: { name: '', email: '' },
  domain: '',
  footerText: 'Qualquer dúvida é só responder este e-mail.',
  signature: 'Atenciosamente'
};

export const ProductEmailConfigPage: React.FC = () => {
  const { productId } = useFilters();
  const productsById = useProductsMap();
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testReplyTo, setTestReplyTo] = useState('');
  const [testAttachment, setTestAttachment] = useState<File | null>(null);
  const { showSuccess, showError } = useMessage();
  const { setRefreshCallback } = useRefresh();

  const loadConfig = useCallback(async () => {
    if (!productId?.trim()) return;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await apiService.getProductEmailConfig();
      if (response.data.success) {
        setConfig(response.data.data || { ...EMPTY_CONFIG });
      }
    } catch (error) {
      setConfig(null);
      setLoadError(error);
      logger.error('Error loading product email config:', error);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig, productId]);

  useEffect(() => {
    setRefreshCallback(loadConfig);
    return () => setRefreshCallback(null);
  }, [loadConfig, setRefreshCallback]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = { ...config };

      // Se a API key estiver vazia, não enviar no update (preserva a existente)
      if (dataToSave.smtp?.auth?.pass === '' || !dataToSave.smtp?.auth?.pass) {
        if (dataToSave.smtp?.auth) {
          const { pass, ...authRest } = dataToSave.smtp.auth;
          dataToSave.smtp = { ...dataToSave.smtp, auth: authRest };
        }
      }

      const response = await apiService.updateProductEmailConfig(dataToSave);
      if (response.data.success) {
        showSuccess('Configuração de email do produto salva com sucesso!');
        const updatedConfig = response.data.data;
        if (config.smtp?.auth?.pass && !dataToSave.smtp?.auth?.pass) {
          updatedConfig.smtp = {
            ...updatedConfig.smtp,
            auth: { ...updatedConfig.smtp?.auth, pass: config.smtp.auth.pass }
          };
        }
        setConfig(updatedConfig);
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleTestAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file && file.type !== 'application/pdf') {
      showError('Apenas arquivos PDF são permitidos no anexo de teste');
      e.target.value = '';
      return;
    }
    setTestAttachment(file);
  };

  const handleTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      showError('Por favor, informe um email válido');
      return;
    }
    setTesting(true);
    try {
      const response = await apiService.testProductEmail(
        testEmail,
        testReplyTo || undefined,
        testAttachment || undefined
      );
      if (response.data.success) {
        showSuccess('Email de teste enviado com sucesso!');
        setTestEmail('');
        setTestAttachment(null);
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao enviar email de teste');
    } finally {
      setTesting(false);
    }
  };

  return (
    <ProductScopeGate
      productId={productId}
      integration="core"
      loading={loading}
      loadError={loadError}
      isEmpty={false}
      onRetry={loadConfig}
    >
      {!config ? (
        <Alert severity="info">Configuração de email ainda não disponível para este produto.</Alert>
      ) : (
        <Box>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="h4">Configuração de E-mail</Typography>
                <Typography variant="body2" color="text.secondary">
                  Cada produto usa sua própria API key (Resend) e domínio de envio — não compartilha
                  a credencial do core geral (vover.com.br).
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <ProductCell
                    productId={productId}
                    name={productsById.get(productId)?.displayName}
                    avatarUrl={productsById.get(productId)?.avatarUrl}
                  />
                  <Chip
                    size="small"
                    color="primary"
                    label={config.enabled ? 'Envio habilitado' : 'Envio desabilitado'}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={config.useApi ? 'API (Resend)' : 'SMTP'}
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Provedor e credenciais
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              A API key vazia não sobrescreve o segredo já salvo. Preencha apenas quando for trocar
              a credencial.
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={e => setConfig({ ...config, enabled: e.target.checked })}
                />
              }
              label="Habilitar envio de emails deste produto"
              sx={{ mb: 3 }}
            />

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Método de Envio</FormLabel>
              <RadioGroup
                row
                value={config.useApi ? 'api' : 'smtp'}
                onChange={e => setConfig({ ...config, useApi: e.target.value === 'api' })}
              >
                <FormControlLabel value="smtp" control={<Radio />} label="SMTP" />
                <FormControlLabel value="api" control={<Radio />} label="API (Resend)" />
              </RadioGroup>
            </FormControl>

            {config.useApi ? (
              <>
                <TextField
                  fullWidth
                  label="API Key"
                  type="password"
                  value={config.smtp?.auth?.pass || ''}
                  onChange={e =>
                    setConfig({
                      ...config,
                      smtp: { ...config.smtp, auth: { ...config.smtp?.auth, pass: e.target.value } }
                    })
                  }
                  margin="normal"
                  helperText="Chave API do Resend deste produto"
                />
                <TextField
                  fullWidth
                  label="Domínio de envio"
                  value={config.domain || ''}
                  onChange={e => setConfig({ ...config, domain: e.target.value })}
                  margin="normal"
                  placeholder="ex.: vcondo.com.br"
                  helperText="Domínio verificado (SPF/DKIM) no Resend. Ao salvar, atualiza a allowlist de remetentes no vover-mail para este produto."
                />
                <TextField
                  fullWidth
                  label="Webhook Secret"
                  type="password"
                  value={config.webhookSecret || ''}
                  onChange={e => setConfig({ ...config, webhookSecret: e.target.value })}
                  margin="normal"
                  helperText={
                    'Signing secret do webhook Resend deste produto (aba Webhooks no dashboard ' +
                    'Resend). Configure a URL do webhook como .../webhooks/resend/' +
                    (productId || '<productId>') +
                    ' — necessário para processar bounce/complaint automaticamente.'
                  }
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Host SMTP"
                  value={config.smtp?.host || ''}
                  onChange={e =>
                    setConfig({ ...config, smtp: { ...config.smtp, host: e.target.value } })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Porta"
                  type="number"
                  value={config.smtp?.port || ''}
                  onChange={e =>
                    setConfig({
                      ...config,
                      smtp: { ...config.smtp, port: parseInt(e.target.value, 10) }
                    })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Usuário"
                  value={config.smtp?.auth?.user || ''}
                  onChange={e =>
                    setConfig({
                      ...config,
                      smtp: { ...config.smtp, auth: { ...config.smtp?.auth, user: e.target.value } }
                    })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Senha"
                  type="password"
                  value={config.smtp?.auth?.pass || ''}
                  onChange={e =>
                    setConfig({
                      ...config,
                      smtp: { ...config.smtp, auth: { ...config.smtp?.auth, pass: e.target.value } }
                    })
                  }
                  margin="normal"
                />
              </>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Remetente padrão
            </Typography>
            <TextField
              fullWidth
              label="Nome do Remetente"
              value={config.sender?.name || ''}
              onChange={e =>
                setConfig({ ...config, sender: { ...config.sender, name: e.target.value } })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Email do Remetente"
              type="email"
              value={config.sender?.email || ''}
              onChange={e =>
                setConfig({ ...config, sender: { ...config.sender, email: e.target.value } })
              }
              margin="normal"
              helperText="Deve pertencer ao domínio de envio configurado acima"
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Teste de entrega
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Envie um e-mail de teste antes de considerar a configuração pronta para produção.
            </Typography>
            <Box
              sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}
            >
              <TextField
                label="Email para teste"
                type="email"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="seu@email.com"
                sx={{ flex: 1, minWidth: 220 }}
              />
              <TextField
                label="Reply-To (opcional)"
                type="email"
                value={testReplyTo}
                onChange={e => setTestReplyTo(e.target.value)}
                placeholder="responder-para@exemplo.com"
                helperText="Simula o Reply-To usado nos envios reais do produto"
                sx={{ flex: 1, minWidth: 220 }}
              />
              <Button
                variant="outlined"
                onClick={handleTestEmail}
                disabled={testing || !testEmail}
                sx={{ mt: 1 }}
              >
                {testing ? 'Enviando...' : 'Testar Envio'}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Button variant="text" component="label" size="small">
                {testAttachment ? 'Trocar anexo (PDF)' : 'Anexar PDF (opcional)'}
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={handleTestAttachmentChange}
                />
              </Button>
              {testAttachment ? (
                <Chip
                  label={testAttachment.name}
                  size="small"
                  onDelete={() => setTestAttachment(null)}
                />
              ) : null}
            </Box>

            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ mt: 2 }}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </Paper>
        </Box>
      )}
    </ProductScopeGate>
  );
};

export default ProductEmailConfigPage;
